/**
 * Places Suggestion Service
 * 
 * This service listens for events from other modules (like accommodation additions)
 * and suggests places creation based on that information.
 */

import { eventBridgeService, EVENT_TYPES } from '@trip-planner-monorepo/shared';
const placesService = require('./placesService');

/**
 * Initializes the places suggestion service
 * This should be called when the application starts
 */
const initPlacesSuggestionService = () => {
  // Subscribe to accommodation added events
  eventBridgeService.subscribe(EVENT_TYPES.ACCOMMODATION_ADDED, handleAccommodationAdded);
  // Subscribe to accommodation city changed events
  eventBridgeService.subscribe(EVENT_TYPES.ACCOMMODATION_CITY_CHANGED, handleAccommodationAdded);
  
  // Subscribe to place suggestion acceptance events
  eventBridgeService.subscribe(EVENT_TYPES.SUGGESTION_ACCEPTED, handlePlaceSuggestionAccepted);
  
  console.log('Places suggestion service initialized');
};

/**
 * Handles the ACCOMMODATION_ADDED event
 * Creates place suggestions based on accommodation city information
 * 
 * @param {Object} accommodationData - The newly created accommodation data
 */
const handleAccommodationAdded = async (accommodationData) => {
  try {
    console.log('Handling accommodation added event:', accommodationData);
    
    const { id: accommodationId, tripId, details } = accommodationData;
    
    // Check if the accommodation has city information
    if (!details?.city) {
      console.log('No city information in accommodation, skipping place suggestion');
      return;
    }
    
    const userId = accommodationData.userId; // Assuming userId is included in the event payload
    
    // Fetch existing places to check if this city already exists
    const existingPlaces = await placesService.fetchPlaces(userId, tripId);
    
    // Check if a place with this city name already exists
    const existingCityPlace = existingPlaces.find(place => 
      place.name.toLowerCase() === details.city.toLowerCase() ||
      (place.fullName && place.fullName.toLowerCase().includes(details.city.toLowerCase()))
    );
    
    if (existingCityPlace) {
      console.log(`City ${details.city} already exists as a place, suggesting link to accommodation`);
      // Suggest linking to an existing place
      eventBridgeService.publish(EVENT_TYPES.PLACE_SUGGESTION, {
        type: 'LINK_TO_EXISTING',
        userId,
        tripId,
        accommodationId,
        placeId: existingCityPlace.id,
        cityName: details.city,
        placeName: existingCityPlace.name,
        accommodationName: accommodationData.summary || details.name || 'New accommodation',
        message: `Link "${accommodationData.summary || details.name || 'accommodation'}" to existing place "${existingCityPlace.name}"?`
      });
    } else {
      console.log(`Suggesting new place creation for city ${details.city}`);
      // Suggest creating a new place
      const suggestedPlaceData = {
        name: details.city,
        fullName: details.city,
        context: details.country || '',
        latitude: null,
        longitude: null,
        type: 'city',
        source: 'accommodation',
        sourceId: accommodationId
      };
      
      // Publish suggestion event
      eventBridgeService.publish(EVENT_TYPES.PLACE_SUGGESTION, {
        type: 'CREATE_NEW',
        userId,
        tripId,
        accommodationId,
        placeData: suggestedPlaceData,
        cityName: details.city,
        accommodationName: accommodationData.summary || details.name || 'New accommodation',
        message: `Create a new place for "${details.city}" from your accommodation?`
      });
    }
  } catch (error) {
    console.error('Error handling accommodation added event:', error);
  }
};

/**
 * Handles the acceptance of a place suggestion
 * 
 * @param {Object} suggestionData - The suggestion data containing user's choice
 */
const handlePlaceSuggestionAccepted = async (suggestionData) => {
  try {
    console.log('Handling place suggestion acceptance:', suggestionData);
    
    const { type, userId, tripId, accommodationId } = suggestionData;
    
    if (type === 'CREATE_NEW') {
      // Create a new place based on the suggestion
      const placeId = await placesService.createPlace(userId, tripId, suggestionData.placeData);
      console.log(`Created new place with ID: ${placeId}`);
      
      // Now link the accommodation to this new place
      eventBridgeService.publish(EVENT_TYPES.SUGGESTION_CREATED, {
        type: 'UPDATE_ACCOMMODATION_PLACE_REFERENCE',
        placeId,
        accommodationId,
        tripId,
        userId,
        message: `Place "${suggestionData.placeData.name}" has been created and linked to accommodation`
      });
    } else if (type === 'LINK_TO_EXISTING') {
      // Link the accommodation to the existing place
      eventBridgeService.publish(EVENT_TYPES.SUGGESTION_CREATED, {
        type: 'UPDATE_ACCOMMODATION_PLACE_REFERENCE',
        placeId: suggestionData.placeId,
        accommodationId,
        tripId,
        userId,
        message: `Accommodation has been linked to place "${suggestionData.placeName}"`
      });
    }
    
  } catch (error) {
    console.error('Error handling place suggestion acceptance:', error);
  }
};

export const placesSuggestionService = {
  init: initPlacesSuggestionService
}; 